import { useState, useCallback, useMemo } from 'react';
import FileSaver from 'file-saver';
import { ExportModel } from 'features/Export/ExportForm';
import { ExportDataModel, FileResponse } from 'services/tenantManagementService';
import ExportToFile, { defaultExportTypeOptions } from './ExportToFile';
import { tryCatchExportFileByAction } from 'utils/exportUtils';
import { GenericColumnModel, FilterSortPageType } from 'components/Table';
import { BackendColumnFieldsMap, usePrepareExportModel } from 'features/TableColumns/prepareExportColumns';
import { RadioItemType } from 'components/Form';
import { useGetTranslation } from 'components/Translations/translationUtils';

export type ExportFormFlags = {
	forceApplyFilters?: boolean
	forceApplySort?: boolean
	forceUseOnlyVisible?: boolean
	forceExportOnlyCurrentPage?: boolean
	hideApplyFilters?: boolean
	hideApplySort?: boolean
	hideUseOnlyVisible?: boolean
	hideExportOnlyCurrentPage?: boolean
}

type Props = ExportFormFlags & {
	visibleColumns: GenericColumnModel[]
	hiddenColumns: GenericColumnModel[]
	specificBackendColumnFieldsMap?: BackendColumnFieldsMap
	filterSortPage: FilterSortPageType
	exportAction(exportDataModel: ExportDataModel, exportModel?: ExportModel): Promise<FileResponse>
	title: string
    exportTypeOptions?: RadioItemType[]
	createExportModelFunction?: (visibleColumns: GenericColumnModel[], hiddenColumns: GenericColumnModel[], specificBackendColumnFieldsMap: BackendColumnFieldsMap, exportModel: ExportModel, filterSortPage: FilterSortPageType) => ExportDataModel
}

export const Export = ({
	visibleColumns,
	hiddenColumns,
	specificBackendColumnFieldsMap,
	filterSortPage,
	exportAction,
	title,
	exportTypeOptions = defaultExportTypeOptions,
	forceApplyFilters,
	forceApplySort,
	forceUseOnlyVisible,
	forceExportOnlyCurrentPage,
	hideApplyFilters,
	hideApplySort,
	hideUseOnlyVisible,
	hideExportOnlyCurrentPage,
	createExportModelFunction
}: Props) => {
	const [loadingExport, setLoadingExport] = useState(false);
	const prepareExportModel = usePrepareExportModel();
	const getTranslation = useGetTranslation();

	const createExportFunctionMemo = useMemo(
		() => createExportModelFunction || prepareExportModel,
		[createExportModelFunction, prepareExportModel]
	)

	const exportCallback = useCallback(
		async (exportModel: ExportModel) => {
			const exportDataModel = createExportFunctionMemo(visibleColumns, hiddenColumns, specificBackendColumnFieldsMap || {}, exportModel, filterSortPage);
			exportDataModel.title = getTranslation(title);

			setLoadingExport(true);

			const bindedAction = exportAction.bind(null, exportDataModel, exportModel);
			const response = await tryCatchExportFileByAction(bindedAction);
			if (response.success && response.value) {
				FileSaver.saveAs(response.value.data, response.value.fileName);
			}

			setLoadingExport(false);
		},
		[createExportFunctionMemo, visibleColumns, hiddenColumns, specificBackendColumnFieldsMap, filterSortPage, getTranslation, title, exportAction]
	)

	return (
		<ExportToFile
			onExport={exportCallback}
			loading={loadingExport}
			exportTypeOptions={exportTypeOptions}
			forceApplyFilters={forceApplyFilters}
			forceApplySort={forceApplySort}
			forceUseOnlyVisible={forceUseOnlyVisible}
			forceExportOnlyCurrentPage={forceExportOnlyCurrentPage}
			hideApplyFilters={hideApplyFilters}
			hideApplySort={hideApplySort}
			hideUseOnlyVisible={hideUseOnlyVisible}
			hideExportOnlyCurrentPage={hideExportOnlyCurrentPage}
		/>
	)
}
